// src/components/notifications/MessageNotification.tsx
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSession } from "next-auth/react";
import { MessageSquare, X, CheckCircle } from 'lucide-react';
import './MessageNotification.css';

interface MessageNotificationProps {
  disabled?: boolean;
  delay?: number; 
}

interface NotificationModalProps {
  isOpen: boolean;
  unreadCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}

const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  unreadCount,
  onConfirm,
  onCancel
}) => {
  if (!isOpen) return null;

  // Gérer les clics sur l'overlay pour fermer le modal
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div className="notification-modal-overlay" onClick={handleOverlayClick}>
      <div className="notification-modal">
        {/* Header */}
        <div className="notification-modal-header">
          <div className="notification-modal-icon-wrapper">
            <div className="notification-modal-icon">
              <MessageSquare size={24} />
            </div>
            <div>
              <h3 className="notification-modal-title">
                Nouveaux messages
              </h3>
              <p className="notification-modal-subtitle">
                {unreadCount} message{unreadCount > 1 ? 's' : ''} non lu{unreadCount > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button onClick={onCancel} className="notification-modal-close">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="notification-modal-content">
          <p className="notification-modal-text">
            {unreadCount === 1 
              ? `Vous avez reçu ${unreadCount} nouveau message. Souhaitez-vous le consulter maintenant ?`
              : `Vous avez reçu ${unreadCount} nouveaux messages. Souhaitez-vous les consulter maintenant ?`
            }
          </p>
        </div>

        {/* Actions */}
        <div className="notification-modal-actions">
          <button onClick={onCancel} className="notification-modal-btn notification-modal-btn-secondary">
            Plus tard
          </button>
          <button onClick={onConfirm} className="notification-modal-btn notification-modal-btn-primary">
            <CheckCircle size={18} />
            Consulter
          </button>
        </div>
      </div>
    </div>
  );
};

const MessageNotification: React.FC<MessageNotificationProps> = ({ 
  disabled = false, 
  delay = 800 
}) => {
  const router = useRouter();
  const { data: session, status } = useSession();
  const [showModal, setShowModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Nettoyer sessionStorage à la déconnexion
  useEffect(() => {
    if (status === "unauthenticated") {
      sessionStorage.removeItem('message_notification_shown');
      setShowModal(false);
    }
  }, [status]);

  useEffect(() => {
    // Seulement si connecté et pas disabled
    if (status !== "authenticated" || disabled) {
      return;
    }

    // Vérifier si la notification a déjà été affichée dans cette session
    const hasShownNotification = sessionStorage.getItem('message_notification_shown');
    
    if (hasShownNotification) {
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const response = await fetch('/api/messages/unread-count');
        
        if (response.ok) {
          const data = await response.json();
          const count = data.count || 0;
          
          if (count > 0) {
            setUnreadCount(count);
            setShowModal(true);
            // Marquer comme affiché pour toute cette session
            sessionStorage.setItem('message_notification_shown', 'true');
          }
        }
      } catch (error) {
        console.error('Erreur lors de la vérification des messages:', error);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [status, disabled, delay]);

  const handleConfirm = () => {
    setShowModal(false);
    router.push('/messages');
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  return (
    <NotificationModal
      isOpen={showModal}
      unreadCount={unreadCount}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );
};

export default MessageNotification;